'use client';
import { Box } from '@radix-ui/themes';
import { ArrowUpRight } from 'lucide-react';
import Link from 'next/link';
import React from 'react';
import { motion } from 'framer-motion';
import { useFetchUser } from '@/auth/hooks';
import { explorerAnalytics } from '@/lib/analytics';

interface ApiCardProps {
  endpoint: string;
  method: string; // 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  name: string;
  description: string;
  actionURL: string;
  actionLabel?: string;
  resource?: string;
}

const getMethodColor = (method: ApiCardProps['method']) => {
  switch (method) {
    case 'GET':
      return 'bg-emerald-100 text-emerald-800 border-emerald-300';
    case 'POST':
      return 'bg-blue-100 text-blue-800 border-blue-300';
  }
};

// Animation variants
const cardVariants = {
  hover: {
    y: -3,
    boxShadow: '0px 6px 12px rgba(0, 0, 0, 0.07)',
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 15,
    },
  },
};

const methodBadgeVariants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { duration: 0.3 },
  },
};

const endpointVariants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { duration: 0.3 },
  },
};

const textVariants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { duration: 0.3 },
  },
};

const buttonVariants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { duration: 0.3 },
  },
  hover: {
    scale: 1.03,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 15,
    },
  },
};

const APICard: React.FC<ApiCardProps> = ({
  endpoint,
  method,
  name,
  description,
  actionURL,
  actionLabel = 'Try it in Playground',
  resource = '',
}) => {
  const { isLoggedIn } = useFetchUser();
  return (
    <Link
      href={actionURL}
      passHref
      onClick={() => {
        explorerAnalytics.endpointNavigated({
          resource,
          endpoint: name || '',
          httpMethod: method || '',
          navigationSource: 'ai_recommendation',
          isLoggedIn: !!isLoggedIn,
        });
      }}
    >
      <motion.div
        className="border rounded-lg shadow-sm p-4 bg-white cursor-pointer mb-6"
        whileHover="hover"
        variants={cardVariants}
        initial="initial"
        animate="animate"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center">
            <motion.span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium mr-2 ${getMethodColor(method)}`}
              variants={methodBadgeVariants}
            >
              {method}
            </motion.span>
            <motion.h3
              className="font-mono truncate text-[#012a38]"
              style={{ fontSize: '0.8rem' }}
              variants={endpointVariants}
            >
              {endpoint}
            </motion.h3>
          </div>
        </div>
        <motion.p
          className="text-sm text-gray-600 mb-2"
          variants={textVariants}
        >
          {name}
        </motion.p>
        <motion.p
          className="text-sm text-gray-500 italic mb-4"
          variants={textVariants}
        >
          {description}
        </motion.p>

        <div className="flex justify-start mt-2">
          <motion.div variants={buttonVariants} whileHover="hover">
            <Box
              className={`rt-Box flex gap-1 text-sm justify-center items-center text-[#012a38] font-normal leading-none tracking-tighter bg-white border-2 border-[#012a38] py-1 px-2 font-inter self-start`}
            >
              <b>{actionLabel}</b>
              <ArrowUpRight />
            </Box>
          </motion.div>
        </div>
      </motion.div>
    </Link>
  );
};

export default APICard;
